import { defineStore } from "pinia";

import axios from "../../axios-auth";
import qs from "qs";
import * as R from "ramda";
import { getMediaUrl } from "../../helpers/helpers";

import { useChangeOptionsStore } from "@/stores/modules/changeOptions";
import { useCheckedPageOptionsStore } from "@/stores/modules/checkedPageOptions";
import { useMyAccountStore } from "@/stores/modules/myAccount";
import { usePersonalizeStore } from "@/stores/modules/personalize";
import { useUploadsStore } from "@/stores/modules/uploads";
import { useCustomStore } from "@/stores/modules/custom";
import { useConfigStore } from "@/stores/modules/config";
import { useBundleStore } from "@/stores/modules/bundle";
import { useUrlsStore } from "@/stores/modules/urls";
import { usePreview3DStore } from "@/stores/modules/preview3D";
import { useQuantityFieldStore } from "@/stores/modules/quantityField";
import { usePreflightPitstopStore } from "@/stores/modules/preflightPitstop";
import { usePreflightDataStore } from "@/stores/modules/preflightData";
import { usePackingOptionsStore } from "@/stores/modules/packingOptions";
import { useCustomerStore } from "@/stores/modules/customer";
import { useUpdatesStore } from "@/stores/modules/updates";
import { useEditorFilesStore } from "@/stores/modules/editorFiles";
import { useWebproductStore } from "./webproduct";
import { callbackService } from "../../helpers/callbackService";

export const useImageEditorStore = defineStore("imageeditor", {
  state: () => ({
    defaults: {
      oieContainer: "#oie-container",
      editor: "#canvasArea",
      format: "#format-layer",
      marginViewPlaceHolder: ".marginViewPlaceHolder",
      generate: "#generatePdf",
      carousel: "#lpf_gallery_carousel",
      next: "#lfp_gallery_next",
      prev: "#lfp_gallery_prev",
      galleryItem: "#lpf_gallery_carousel .image_gallery_item",
      mirroredMargins: {
        top: ".marginViewPlaceHolder .topMirrored",
        bottom: ".marginViewPlaceHolder .bottomMirrored",
        left: ".marginViewPlaceHolder .leftMirrored",
        right: ".marginViewPlaceHolder .rightMirrored"
      },
      formatCropLayer: "#format-crop-layer",
      detectable:
        "#product_addtocart_form #product_part_container select.print_options",
      monochromatic_container: "#margin_color_container",
      monochromatic_input: "#margin_color_input",
      rounded_container: "#rounded_container",
      is_rotated_input: "#is_rotated_input",
      check_monochromatic: 0,
      addToCartButton: ".button.btn-cart",
      steckdosenEditor: "#steckdosenEditor",
      onlineEditor: "#onlineEditor",
      pdfImageDefault: {
        filename: "",
        path: "",
        cropX: 0,
        cropY: 0,
        cropX1: 0,
        cropY1: 0,
        cropH: 0,
        cropW: 0,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        type: "normal",
        circle_distance: 0,
        rotate_angle: 0
      },
      imageLayer: "#image-layer"
    },
    PoieVariables: {
      Texts: {
        generalError: "",
        invalidDim: "",
        customFormat: {
          label: "Custom Format",
          cm: "cm",
          formatText: "Format",
          widthEmpty: "Please input a width!",
          widthNumber: "Width must	be a number!",
          widthHigher: "Width must be smaller than %value%!",
          widthLower: "Width must be greater than %value%!",
          heightEmpty: "Please input a height!",
          heightNumber: "Height must be a number!",
          heightHigher: "Height must be smaller than %value%!",
          heightLower: "Height must be greater than %value%!",
          cannotChangeFormat: "You cannont change the format",
          alreadyExistFormat: "Format already exist!"
        },
        steckdosen: {
          remove: "Are you sure you want to delete this?",
          exists: "This dosen already exist!"
        },
        margins: {
          negativeValue: "Invalid Margin"
        },
        deleteMessage: "Are you sure you want to delete this item?"
      },
      defaults: {
        imageId: "2fac60ae91cdeb39060f5775524a7d04"
      },
      cmToInch: 0.393700787,
      cmToPt: 28.34645669291,
      pxToPt: 0.75,
      ppiQuality: [72, 150, 300, 400, 500],
      allowCustomFormat: 1,
      enableSteckdosen: 0,
      enableCorners: 0,
      enableWhiteMargin: 0,
      enableCutouts: 0,
      splitOiePdf: 0,
      steckdosenLimits: [
        [10, 10, 10, 10],
        [10, 10, 10, 10],
        [10, 10, 10, 10]
      ],
      dosenSizes: [40, 76, 111],
      dosenIconSizes: [40, 40, 40],
      dosenFullWidth: [8, 15.2, 22.2],
      dosenFullHeight: [8, 8, 8, 8]
    },
    activeElement: {
      index: 0,
      type: "main"
    },
    ready: 0,
    showDosen: 1,
    canvasLoaded: 0,
    imagesToLoad: [],
    canvasImage: "",
    editor: {
      width: null,
      height: null,
      dim: {
        imageDimByFormat: {
          width: null,
          height: null,
          width_cm: null,
          height_cm: null,
          unit: 1
        },
        canvasDimByFormat: {
          width: null,
          height: null,
          width_fix: null,
          height_fix: null,
          left: null,
          unit: 1
        },
        canvasImageDimByFormat: {
          width: null,
          height: null,
          left: null,
          whiteleft: null,
          top: null,
          whitetop: null,
          right: null,
          whiteright: null,
          bottom: null,
          whitebottom: null
        },
        canvasFormatDimByFormat: {
          width: null,
          height: null,
          width_fix: null,
          height_fix: null,
          left: null,
          top: null,
          bottom: null,
          right: null
        }
      },
      marginView: false,
      rotateAngle: 0,
      zoom: 100,
      focalPoint: {
        x: 0,
        y: 0
      },
      cropMode: 0,
      bckMode: 0
    },
    format: {
      width: 90,
      height: 30,
      type: 1, // 1-horizontal; 2-vertical
      rounded: 0,
      show: 1,
      split: 0
    },
    margin: {
      top: 0,
      whitetop: 0,
      right: 0,
      whiteright: 0,
      bottom: 0,
      whitebottom: 0,
      left: 0,
      whiteleft: 0,
      type: 1, //-1 => inner(enlarged), +1 => outer(mirrored)
      lastChanged: "top",
      subtype: 1, // 1=mirrored, 2-white, 3-monocromatic
      rand_option: 0,
      topDefault: 0,
      rightDefault: 0,
      bottomDefault: 0,
      rightDefault: 0,
      color: "#ffffff"
    },
    object: {
      defaults: {
        movable: 1,
        resizable: 1,
        rotatable: 1,
        hasSnap: 0,
        angle: 0,
        offsetX: 0,
        offsetY: 0,
        imagePath: "",
        relativePath: "",
        relativeSrc: "",
        imageSrc: "",
        elementType: "object",
        ratioWidth: 1,
        ratioHeight: 1,
        itemType: "",
        isPdf: 0,
        name: ""
      },
      objects: []
    },
    corners: { corners: [] },
    cutouts: {
      defaults: {
        movable: 1,
        resizable: 0,
        hasSnap: 0,
        rotatable: 0,
        angle: 0,
        offsetX: 5,
        offsetY: 5,
        imagePath: "",
        relativePath: "",
        relativeSrc: "/media/personalization/cutouts/",
        imageSrc: "",
        width: 10,
        height: 10,
        name: ""
      },

      cutouts: []
    },
    sockets: {},
    steckdosen: {
      defaults: {
        movable: 1,
        resizable: 0,
        hasSnap: 0,
        rotatable: 0,
        angle: 0,
        offsetX: 4,
        offsetY: 4,
        imagePath: "",
        relativePath: "",
        relativeSrc: "/media/personalization/sockets/",
        imageSrc: "",
        name: ""
      },
      dosens: []
    },
    canvas: {
      original: {
        width: null,
        height: null,
        unit: 1
      },
      resized: {
        width: null,
        height: null,
        unit: 1
      }
    },
    crop: {
      cropX: null,
      cropY: null,
      cropW: null,
      cropH: null,
      marginT: 0,
      marginR: 0,
      marginB: 0,
      marginL: 0,
      rounded: 0
    },
    image: {
      original: {
        path: null,
        filename: null,
        width: null,
        height: null
      },
      resized: {
        src: null,
        width: null,
        height: null,
        inProgress: 0
      },
      uploadedFile: 0
    },
    thickness: {
      thickness: 0
    },
    galleryImages: []
  }),

  getters: {
    getCanvasLoaded(state) {
      return state.canvasLoaded || 0;
    },
    getImagesToLoad(state) {
      return state.imagesToLoad || [];
    },
    getCanvasImage(state) {
      return state.canvasImage || "";
    },
    getGalleryImages(state) {
      return state.galleryImages || [];
    },
    getImageGalleryItems(state) {
      const editorFilesStore = useEditorFilesStore();
      const urlsStore = useUrlsStore();
      const editorFiles = editorFilesStore.getEditorFiles;
      const itemsIds = this.getGalleryImages;
      const baseUrl = urlsStore.getUrl("mediaBaseUrl");

      const items = R.filter(file => {
        if (R.indexOf(file.id, itemsIds)) return true;
        return false;
      }, editorFiles);

      return R.mapObjIndexed(item => {
        return {
          id: item.uuid,
          url: getMediaUrl(
            R.pathOr("", ["thumbnails", "thumb", "url"], item),
            baseUrl
          ),
          size: R.pathOr("", ["thumbnails", "thumb", "size"], item),
          data: item
        };
      }, items);
    },
    getMargin(state) {
      return state.margin;
    },
    getDefaultSockets(state) {
      return state.sockets;
    },
    getFormat(state) {
      return state.format;
    },
    getPOIEVariables(state) {
      return state.PoieVariables;
    },
    getEditor(state) {
      return state.editor;
    },
    getReady(state) {
      return state.ready;
    },
    getObject(state) {
      return state.object;
    },
    getCutouts(state) {
      return state.cutouts;
    },
    getSteckdosen(state) {
      return state.steckdosen;
    },
    getCanvas(state) {
      return state.canvas;
    },
    getCrop(state) {
      return state.crop;
    },
    getActiveElement(state) {
      return state.activeElement;
    },
    getThickness(state) {
      return state.thickness;
    },
    getCorners(state) {
      return state.corners;
    },
    getImage(state) {
      return state.image;
    },
    getResizedImage(state) {
      return state.image.resized.src;
    },
    //editor editor
    getEditorSize(state) {
      return {
        width: state.editor.width,
        height: state.editor.height
      };
    },
    getRotateAngle(state) {
      return state.editor.rotateAngle;
    },
    getFocalPoint(state) {
      return state.editor.focalPoint;
    },
    pdfUnit(state) {
      return state.editor.dim.imageDimByFormat.unit;
    },
    canvasFormatDimByFormat(state) {
      return {
        width: state.editor.dim.canvasFormatDimByFormat.width,
        height: state.editor.dim.canvasFormatDimByFormat.height,
        width_fix: state.editor.dim.canvasFormatDimByFormat.width_fix,
        height_fix: state.editor.dim.canvasFormatDimByFormat.height_fix,
        left: state.editor.dim.canvasFormatDimByFormat.left,
        top: state.editor.dim.canvasFormatDimByFormat.top,
        bottom: state.editor.dim.canvasFormatDimByFormat.bottom,
        right: state.editor.dim.canvasFormatDimByFormat.right
      };
    },
    canvasFormatLeft(state) {
      return state.editor.dim.canvasDimByFormat.left;
    },
    /* end editor getters */
    /*loadingEditor getters */
    getShow(state) {
      return !state.ready;
    },
    /* endLoadingEditor getters*/
    /*store getters*/
    getResizePercentage(state) {
      return {
        rW: state.image.resized.width / state.image.original.width,
        rH: state.image.resized.height / state.image.original.height
      };
    },
    getFullMargins(state) {
      return {
        width:
          state.margin.left +
          state.margin.right +
          2 * state.thickness.thickness,
        height:
          state.margin.top + state.margin.bottom + 2 * state.thickness.thickness
      };
    },
    getData(state) {},
    getMarginsWithThickness(state) {
      return {
        left: state.margin.left + state.thickness.thickness,
        whiteleft: state.margin.whiteleft,
        whiteright: state.margin.whiteright,
        whitetop: state.margin.whitetop,
        whitebottom: state.margin.whitebottom,
        right: state.margin.right + state.thickness.thickness,
        bottom: state.margin.bottom + state.thickness.thickness,
        top: state.margin.top + state.thickness.thickness,
        type: state.margin.type,
        rand_option: state.margin.rand_option,
        color: state.margin.color,
        subtype: state.margin.subtype
      };
    },
    getFullFormatDim(state) {
      return {
        width:
          state.format.width +
          state.margin.left +
          state.margin.right +
          2 * state.thickness.thickness,
        height:
          state.format.height +
          state.margin.top +
          state.margin.bottom +
          2 * state.thickness.thickness
      };
    },
    getSplitMargins(state) {
      return {
        left: state.margin.left + state.thickness.thickness,
        right: state.margin.right + state.thickness.thickness
      };
    },
    getLayerItems(state) {
      return function (type) {
        /* var activeElement = null;
    
                            switch(state.activeElement.type){
                              case "objects":
                                activeElement = state.object.objects[state.activeElement.index];
                              case "steckdosen":
                                activeElement = state.steckdosen.dosens[state.activeElement.index];
                            } */

        switch (type) {
          case "objects":
            return state.object.objects;
          case "steckdosen":
            return state.steckdosen.dosens;
            break;
          case "cutouts":
            return state.cutouts.cutouts;
            break;
          default:
            return [];
        }
      };
    },
    getLayerItemsDefaults(state) {
      return function (type) {
        switch (type) {
          case "objects":
            return state.object.defaults;
          case "steckdosen":
            return state.steckdosen.defaults;
          case "cutouts":
            return state.cutouts.defaults;
          default:
            return [];
        }
      };
    },
    getActiveElement(state) {
      return state.activeElement;
    },
    /*end store getters*/
    /*-------------canvas gettters--------------- */
    getCanvasOriginalSize(state) {
      return state.canvas.original;
    },
    getCanvasResizedSize(state) {
      return state.canvas.resized;
    },
    /*-------------end canvas gettters--------------- */
    /*-------------crop gettters--------------- */
    getFormatPosition(state) {
      return {
        left: 20
      };
    },
    /*-------------end crop gettters--------------- */
    /*-------------format gettters--------------- */
    isRounded(state) {
      return state.format.rounded;
    },
    split(state) {
      return state.format.split;
    },
    /*-------------end format gettters--------------- */
    /*-------------image gettters--------------- */
    imagePath(state) {
      return state.image.original.path + state.image.original.filename;
    },
    originalImageSize(state) {
      return {
        width: state.image.original.width,
        height: state.image.original.height
      };
    },
    originalFilename(state) {
      return state.image.original.filename;
    },
    originalPath(state) {
      return state.image.original.path;
    },
    uploadedFile(state) {
      return state.image.uploadedFile;
    },
    /*-------------end image gettters--------------- */

    dummygetter(state) {
      return 1;
    },
    getDefaults(state) {
      return state.defaults;
    },
    getBckMode(state) {
      return state.editor.bckMode;
    },
    getMarginColor(state) {
      return state.margin.color || "#ffffff";
    },
    getEnableWhiteMargin(state) {
      return parseInt(state.PoieVariables.enableWhiteMargin);
    },
    getHasWhiteMargin(state) {
      return parseInt(state.PoieVariables.enableWhiteMargin);
    }
  },

  actions: {
    changeGalleryImages(galleryImages) {
      this.galleryImages = galleryImages || [];
    },
    changeReady(ready) {
      this.ready = ready;
    },
    addToActiveElement(active) {
      this.activeElement = active;
    },
    emptyActiveElement(payload) {
      this.activeElement = {
        index: 0,
        type: "main"
      };
    },
    updateItem(params) {
      const { type, index, props } = params;

      let items = [];
      switch (type) {
        case "objects":
          items = [...this.object.objects];
          items[index] = { ...items[index], ...props };
          this.object.objects = items;
          var item = this.object.objects[index];
          var deviation = {
            x: 0,
            y: 0
          };
          if (item.angle) {
            var tetha = -1 * item["angle"];
            var topleft = {
              x: (item["width"] * -1) / 2,
              y: (item["height"] * -1) / 2
            };
            var topLeftD = {
              x: topleft.x * Math.cos(tetha) - topleft.y * Math.sin(tetha),
              y: topleft.x * Math.sin(tetha) + topleft.y * Math.cos(tetha)
            };
            deviation = {
              x: topleft.x - topLeftD.x,
              y: topLeftD.y - topleft.y
            };
          }
          this.object.objects[index]["deviation"] = deviation;
          break;
        case "steckdosen":
          items = [...this.steckdosen.dosens];
          items[index] = { ...items[index], ...props };
          this.steckdosen.dosens = items;
          break;
        case "cutouts":
          items = [...this.cutouts.cutouts];
          items[index] = { ...items[index], ...props };
          this.cutouts.cutouts = items;
          break;
      }
    },
    updateZoom(params) {
      const { index, zoom } = params;
      this.object.objects[index].zoom += zoom - this.object.objects[index].zoom;
    },
    deleteObject(params) {
      const { index } = params;
      this.object.objects = R.remove(index, 1, this.object.objects);
      //this.activeElement.type = "main";
      //this.activeElement.index = 0;
      // this.commit("emptyActiveElement");
    },
    initCorners(data) {
      this.corners.corners = data;
    },
    changeCornerStatus(code) {
      this.corners.corners["corner_" + code].isActive = !(
        this.corners.corners["corner_" + code].isActive | 0
      );
    },
    changeActiveItem(data) {
      this.corners.corners["corner_" + data.corner_code].subItemIndex =
        data.index;
    },
    addObject(object) {
      this.object.objects.push(object);
    },
    changeCutout(data) {
      try {
        if (typeof data !== "object") throw "";
        if (!data.hasOwnProperty("index")) throw "";
        if (!data.hasOwnProperty("pos")) throw "";
        if (!data.hasOwnProperty("value")) throw "";
        this.cutouts.cutouts[data.index][data.pos] = data.value;
      } catch (e) {}
    },
    addNewCutout(data) {
      var POIEVariables = this.getPOIEVariables;
      var left = (
        (this.format.width - this.cutouts.defaults.width) / 2 +
        this.cutouts.defaults.offsetX
      ).toFixed(2);
      var bottom = (
        (this.format.height - this.cutouts.defaults.height) / 2 +
        this.cutouts.defaults.offsetY
      ).toFixed(2);
      var name = this.cutouts.defaults.name;
      if (data.itemType == "rectangle") {
        name = POIEVariables.rectangleString;
      } else {
        name = POIEVariables.circleString;
      }
      this.cutouts.cutouts.push({
        elementType: "cutouts",
        type: "image",
        subtype: "cutout",
        itemType: data.itemType,
        imageSrc: "",
        left: left,
        bottom: bottom,
        width: this.cutouts.defaults.width,
        height: this.cutouts.defaults.height,
        name: name,
        offsetX: this.cutouts.defaults.offsetX,
        offsetY: this.cutouts.defaults.offsetY
      });
    },
    removeCutout(index) {
      this.cutouts.cutouts.splice(index, 1);
    },
    changeDosen(data) {
      try {
        if (typeof data !== "object") throw "";
        if (!data.hasOwnProperty("index")) throw "";
        if (!data.hasOwnProperty("pos")) throw "";
        if (!data.hasOwnProperty("value")) throw "";
        this.steckdosen.dosens[data.index][data.pos] = parseFloat(
          data.value
        ).toFixed(1);
      } catch (e) {}
    },
    addNewDosen(data) {
      var type = 1;
      var left = 0;
      var bottom = 0;
      var offsetx = this.steckdosen.defaults.offsetX;
      var offsety = this.steckdosen.defaults.offsetY;
      var name = this.steckdosen.defaults.name;

      if (typeof data == "object" && data.hasOwnProperty("type"))
        type = data.type;
      if (typeof data == "object" && data.hasOwnProperty("left"))
        left = parseFloat(data.left).toFixed(1);
      if (typeof data == "object" && data.hasOwnProperty("bottom"))
        bottom = parseFloat(data.bottom).toFixed(1);
      if (typeof data == "object" && data.hasOwnProperty("offsetx"))
        offsetx = parseFloat(data.offsetx).toFixed(1);
      if (typeof data == "object" && data.hasOwnProperty("offsety"))
        offsety = parseFloat(data.offsety).toFixed(1);
      if (typeof data == "object" && data.hasOwnProperty("name"))
        name = data.name;
      this.steckdosen.dosens.push({
        elementType: "steckdosen",
        type: "image",
        subtype: "dummyimage",
        code: data.code,
        imageSrc: data.imageSrc,
        left: left,
        offsetX: offsetx,
        offsetY: offsety,
        bottom: bottom,
        height: data.height,
        name: name,
        width: data.width
      });
    },
    removeDosen(index) {
      this.steckdosen.dosens.splice(index, 1);
    },
    changeMargins(data) {
      if (typeof data == "object") {
        if (data.hasOwnProperty("top") && data.top >= 0) {
          if (this.getHasWhiteMargin) {
            this.margin.whitetop = data.top;
          } else {
            this.margin.top = data.top;
          }
        }
        if (data.hasOwnProperty("right") && data.right >= 0)
          if (this.getHasWhiteMargin) this.margin.whiteright = data.right;
          else this.margin.right = data.right;
        if (data.hasOwnProperty("bottom") && data.bottom >= 0)
          if (this.getHasWhiteMargin) this.margin.whitebottom = data.bottom;
          else this.margin.bottom = data.bottom;
        if (data.hasOwnProperty("left") && data.left >= 0)
          if (this.getHasWhiteMargin) this.margin.whiteleft = data.left;
          else this.margin.left = data.left;
        if (data.hasOwnProperty("type")) this.margin.type = data.type;
        if (data.hasOwnProperty("lastChanged"))
          this.margin.lastChanged = data.lastChanged;
      }
    },
    updateBgOnResize: function (data) {
      var o = this.getDefaults;
      var imageLayer = jQuery(o.imageLayer);

      if (imageLayer.length) {
        var bgDraggable = imageLayer.data("printqBgDraggable");
        if (bgDraggable) {
          var ui = imageLayer.bgDraggable("updateOnResize");
          if (ui) {
            var zoom = this.getEditor.zoom / 100; //cache this somewhere
            this.storeFocalPoint({ ui, zoom });
          }
        }
      }
    },
    changeDefaultMargin(data) {
      if (typeof data == "object") {
        if (data.hasOwnProperty("topDefault") && data.topDefault >= 0)
          this.margin.topDefault = data.topDefault;
        if (data.hasOwnProperty("rightDefault") && data.rightDefault >= 0)
          this.margin.rightDefault = data.rightDefault;
        if (data.hasOwnProperty("bottomDefault") && data.bottomDefault >= 0)
          this.margin.bottomDefault = data.bottomDefault;
        if (data.hasOwnProperty("leftDefault") && data.leftDefault >= 0)
          this.margin.leftDefault = data.leftDefault;
      }
    },
    changeMarginType(type) {
      if (this.getHasWhiteMargin) {
        type = 1;
      }
      this.margin.type = type;
    },
    changeMarginSubtype(subtype) {
      if (this.getHasWhiteMargin) {
        subtype = -1;
      }
      this.margin.subtype = subtype;
    },
    changeMarginColor(color) {
      this.margin.color = color;
    },
    changeRandOption(value) {
      this.margin.rand_option = value;
    },
    changeCrop(crop) {
      this.cropX = crop.cropX;
      this.cropY = crop.cropY;
      this.cropW = crop.cropW;
      this.cropH = crop.cropH;
    },
    changeMargin(margin) {
      this.marginT = margin.Top;
      this.marginR = margin.Right;
      this.marginB = margin.Bottom;
      this.marginL = margin.Left;
    },
    changeCropX(cropX) {
      this.cropX = cropX;
    },
    changeCropY(cropY) {
      this.cropY = cropY;
    },
    changeCropW(cropW) {
      this.crop.cropW = cropW;
    },
    changeCropH(cropH) {
      this.crop.cropH = cropH;
    },
    changeMarginT(marginT) {
      this.crop.marginT = marginT;
    },
    changeMarginR(marginR) {
      this.crop.marginR = marginR;
    },
    changeMarginB(marginB) {
      this.crop.marginB = marginB;
    },
    changeMarginL(marginL) {
      this.crop.marginL = marginL;
    },
    changeFormat(data) {
      if (
        typeof data == "object" &&
        data.hasOwnProperty("prop") &&
        data.hasOwnProperty("value")
      ) {
        if (this.format.hasOwnProperty(data.prop))
          this.format[data.prop] = data.value;
      }
    },
    changeRounded(flag) {
      this.format.rounded = flag;
    },
    changeSplit(split) {
      this.format.split = split;
    },
    changeEditorWidth(width) {
      this.editor.width = width;
    },
    changeEditorHeight(height) {
      this.editor.height = height;
    },
    changeImageDimByFormat(dim) {
      this.editor.dim.imageDimByFormat.height = dim.height;
      this.editor.dim.imageDimByFormat.width = dim.width;
      this.editor.dim.imageDimByFormat.width_cm = dim.width_cm;
      this.editor.dim.imageDimByFormat.height_cm = dim.height_cm;
      this.editor.dim.imageDimByFormat.unit = dim.unit;
    },
    changeCanvasDimByFormat(dim) {
      this.editor.dim.canvasDimByFormat.height = dim.height;
      this.editor.dim.canvasDimByFormat.width = dim.width;
      this.editor.dim.canvasDimByFormat.height_fix = dim.height_fix;
      this.editor.dim.canvasDimByFormat.width_fix = dim.width_fix;
      this.editor.dim.canvasDimByFormat.unit = dim.unit;
      this.editor.dim.canvasDimByFormat.top = dim.top;
      this.editor.dim.canvasDimByFormat.left = dim.left;
    },
    changeCanvasImageDimByFormat(dim) {
      this.editor.dim.canvasImageDimByFormat.height = dim.height;
      this.editor.dim.canvasImageDimByFormat.width = dim.width;
      this.editor.dim.canvasImageDimByFormat.left = dim.left;
      this.editor.dim.canvasImageDimByFormat.whiteleft = dim.whiteleft;
      this.editor.dim.canvasImageDimByFormat.top = dim.top;
      this.editor.dim.canvasImageDimByFormat.whitetop = dim.whitetop;
      this.editor.dim.canvasImageDimByFormat.right = dim.right;
      this.editor.dim.canvasImageDimByFormat.whiteright = dim.whiteright;
      this.editor.dim.canvasImageDimByFormat.bottom = dim.bottom;
      this.editor.dim.canvasImageDimByFormat.whitebottom = dim.whitebottom;
    },
    changeCanvasFormatDimByFormat(dim) {
      this.editor.dim.canvasFormatDimByFormat.height = dim.height;
      this.editor.dim.canvasFormatDimByFormat.width = dim.width;
      this.editor.dim.canvasFormatDimByFormat.height_fix = dim.height_fix;
      this.editor.dim.canvasFormatDimByFormat.width_fix = dim.width_fix;
      this.editor.dim.canvasFormatDimByFormat.left = dim.left;
      this.editor.dim.canvasFormatDimByFormat.top = dim.top;
      this.editor.dim.canvasFormatDimByFormat.bottom = dim.bottom;
      this.editor.dim.canvasFormatDimByFormat.right = dim.right;
    },
    changeCanvasFormatPositionByFormat(dim) {
      this.editor.dim.canvasFormatDimByFormat.left = dim.left;
      this.editor.dim.canvasFormatDimByFormat.top = dim.top;
      this.editor.dim.canvasFormatDimByFormat.bottom = dim.bottom;
      this.editor.dim.canvasFormatDimByFormat.right = dim.right;
    },
    changeMarginView(flag) {
      this.editor.marginView = flag;
    },
    increaseRotateAngle() {
      this.editor.rotateAngle = (this.rotateAngle + 90) % 360;
    },
    changeRotateAngle(angle) {
      this.editor.rotateAngle = angle;
    },
    changeZoom(zoom) {
      this.editor.zoom = zoom;
    },
    changeCropMode(mode) {
      this.editor.cropMode = mode;
    },
    changeBckMode(mode) {
      this.editor.bckMode = mode;
    },
    changeFocalPoint(point) {
      if (typeof point == "object") {
        if (point.hasOwnProperty("x")) this.editor.focalPoint.x = point.x;
        if (point.hasOwnProperty("y")) this.editor.focalPoint.y = point.y;
      }
    },
    changeImageId(imageId) {
      this.image.original.imageId = imageId;
    },
    changeOriginalImageSize(size) {
      this.image.original.width = size.width;
      this.image.original.height = size.height;
    },
    changeOriginalImageWidth(width) {
      this.image.original.width = width;
    },
    changeOriginalImageHeight(height) {
      this.image.original.height = height;
    },
    changeResizedImageSrc(src) {
      this.image.resized.src = src;
    },
    changeResizedImageWidth(width) {
      this.image.resized.width = width;
    },
    changeResizedImageHeight(height) {
      this.image.resized.height = height;
    },
    changeResizedImageSize(size) {
      this.image.resized.width = size.width;
      this.image.resized.height = size.height;
    },
    inProgress(value) {
      this.image.resized.inProgress = value;
    },
    changeUploadedFile(value) {
      this.image.uploadedFile = value;
    },
    changeThickness(thickness) {
      this.thickness.thickness = thickness;
    },
    getResizeImage(data) {},
    changeCheckMonochromatic(data) {
      this.defaults.check_monochromatic = data;
    },
    initImageEditorData(data) {
      if (data.corners) {
        this.corners.corners = data.corners;
      }
      if (data.sockets) {
        this.sockets = data.corners;
      }
    },
    loadImageEditorData(payload) {
      const editorFilesStore = useEditorFilesStore();
      const urlsStore = useUrlsStore();
      if (R.hasPath(["Texts"], payload))
        this.PoieVariables.Texts = {
          ...this.PoieVariables.Texts,
          ...payload.Texts
        };
      if (R.hasPath(["cmToInch"], payload))
        this.PoieVariables.cmToInch = payload.cmToInch;
      if (R.hasPath(["corners"], payload))
        this.corners.corners = payload.corners;
      if (R.hasPath(["defaults"], payload))
        this.PoieVariables.defaults = payload.defaults;
      if (R.hasPath(["dosenFullHeight"], payload))
        this.PoieVariables.dosenFullHeight = payload.dosenFullHeight;
      if (R.hasPath(["dosenFullWidth"], payload))
        this.PoieVariables.dosenFullWidth = payload.dosenFullWidth;
      if (R.hasPath(["dosenIconSizes"], payload))
        this.PoieVariables.dosenIconSizes = payload.dosenIconSizes;
      if (R.hasPath(["dosenSizes"], payload))
        this.PoieVariables.dosenSizes = payload.dosenSizes;
      if (R.hasPath(["enableCorners"], payload))
        this.PoieVariables.enableCorners = parseInt(payload.enableCorners);
      if (R.hasPath(["enableWhiteMargin"], payload)) {
        if (payload.enableWhiteMargin)
          this.PoieVariables.enableWhiteMargin = parseInt(
            payload.enableWhiteMargin
          );
        /*  if (parseInt(payload.enableWhiteMargin)) {
        state.margin.type = -1;
        state.margin.subtype = -1;
      } */
      }
      if (R.hasPath(["enableCutouts"], payload))
        this.PoieVariables.enableCutouts = parseInt(payload.enableCutouts);
      if (R.hasPath(["enableSteckdosen"], payload))
        this.PoieVariables.enableSteckdosen = parseInt(
          payload.enableSteckdosen
        );
      if (R.hasPath(["ppiQuality"], payload))
        this.PoieVariables.ppiQuality = payload.ppiQuality;
      if (R.hasPath(["pxToPt"], payload))
        this.PoieVariables.pxToPt = payload.pxToPt;
      if (R.hasPath(["sockets"], payload))
        this.sockets = {
          ...this.sockets,
          ...payload.sockets
        };
      if (R.hasPath(["splitOiePdf"], payload))
        this.PoieVariables.splitOiePdf = parseInt(payload.splitOiePdf);
      if (R.hasPath(["steckdosenLimits"], payload))
        this.PoieVariables.steckdosenLimits = {
          ...this.PoieVariables.steckdosenLimits,
          ...payload.steckdosenLimits
        };

      const imageGallery = R.pathOr([], ["galleryImages"], payload);

      if (!R.isEmpty(imageGallery)) {
        this.changeGalleryImages(imageGallery);
        const workingsData = imageGallery.map(uuid => {
          return {
            id: uuid
          };
        });

        axios.instance
          .post(urlsStore.getUrl("workingsUrl"), {
            data: workingsData
          })
          .then(response => {
            const { success, data } = response.data;
            if (success) {
              R.forEach(item => {
                const { success, data } = item;
                if (success) {
                  editorFilesStore.addEditorFile(data);
                }
              }, data);
            }
          })
          .catch(error => {
            console.log("Error " + error);
          });
      }
    },
    completePersonalization(payload) {
      const personalizeStore = usePersonalizeStore();
      const webproductStore = useWebproductStore();
      const customerStore = useCustomerStore();
      const changeOptionsStore = useChangeOptionsStore();
      const urlsStore = useUrlsStore();
      var additional_data = payload.onCartDataEncoded;
      var url = urlsStore.getUrl(axios.apiGetCompletePersonalization);
      personalizeStore.setPersonalizeLoading(true);
      var selectionId = webproductStore.getSelectionId;
      const _data = {
        template_id: 0,
        attached: 1,
        session: customerStore.getCustomer,
        source: "lfp",
        project_data: {
          content: JSON.stringify(changeOptionsStore.getAddToCartCallbacks[0]())
        },
        store_id: webproductStore.getStoreCode,
        store_code: webproductStore.getStoreCode,
        product_id: webproductStore.getWebProductId,
        api_url: "/",
        api_template_id: 0,
        additional_data: additional_data,
        success_url: "",
        selection: selectionId,
        customer: customerStore.getCustomer,
        preview: { 0: this.getResizedImage }
      };
      axios.instance
        .post(url, _data)
        .then(response => {
          const { success, data, customizedProductId } = response.data;
          if (success) {
            callbackService.sendCallback(
              callbackService.callbacks.onCartSubmitCallback,
              payload.onCartData
            );
          } else {
            alert(response.message);
          }
          personalizeStore.setPersonalizeLoading(false);
        })
        .catch(error => {
          console.log("Error " + error);
          personalizeStore.setPersonalizeLoading(false);
        });
    },
    removeCutoutAction(index) {
      this.removeCutout(index);
      this.updateCutoutsNumber();
    },
    updateCutoutsNumber() {
      try {
        var formElement = document.getElementById("product_addtocart_form");
        var cutoutsQtyElement = document.getElementById("cutouts_qty");

        if (!formElement) throw false;
        if (!cutoutsQtyElement) {
          var cutoutsQtyElement = document.createElement("input");
          cutoutsQtyElement.type = "hidden";
          cutoutsQtyElement.name = "print_options[cutouts_qty]";
          cutoutsQtyElement.id = "cutouts_qty";
          formElement.appendChild(cutoutsQtyElement);
        }

        cutoutsQtyElement.value = this.cutouts.length;
      } catch (e) {}
    },
    removeDosenAction(index) {
      this.removeDosen(index);
      this.updateDosenNumber();
    },
    updateDosenNumber() {
      try {
        var formElement = document.getElementById("product_addtocart_form");
        var steckdosenQtyElement = document.getElementById("steckdosen_qty");

        if (!formElement) throw false;
        if (!steckdosenQtyElement) {
          var steckdosenQtyElement = document.createElement("input");
          steckdosenQtyElement.type = "hidden";
          steckdosenQtyElement.name = "print_options[steckdosen_qty]";
          steckdosenQtyElement.id = "steckdosen_qty";
          formElement.appendChild(steckdosenQtyElement);
        }

        steckdosenQtyElement.value = this.dosens.length;
      } catch (e) {}
    },
    doPortret(widget) {
      const customStore = useCustomStore();
      var width = this.getFormat.width;
      var height = this.getFormat.height;
      customStore.inputCustomFormat({
        width: Math.min(width, height),
        height: Math.max(width, height)
      });
    },
    doLandscape(widget) {
      const customStore = useCustomStore();
      var width = this.getFormat.width;
      var height = this.getFormat.height;
      customStore.inputCustomFormat({
        width: Math.max(width, height),
        height: Math.min(width, height)
      });
    },
    doChangeFormat(params) {
      var width = params.width;
      var height = params.height;
      width = parseFloat(width);
      height = parseFloat(height);
      this.changeFormat({
        prop: "width",
        value: width
      });
      this.changeFormat({
        prop: "height",
        value: height
      });

      /* for (
        var index = 0;
        index <= _ref.getters.getSteckdosen.dosens.length - 1;
        index++
      ) {
        var value = _ref.getters.getSteckdosen.dosens[index].posx;
        var type = _ref.getters.getSteckdosen.dosens[index].type - 1;
        var size = _ref.getters.getSteckdosen.dosens[index].size;
        var fullWidth = POIEVariables.dosenFullWidth[type];
        var right = POIEVariables.steckdosenLimits[type][1] - size / 2;
        var width = _ref.getters.getFormat.width;
        var height = _ref.getters.getFormat.height;
        var maxX = parseFloat((width - fullWidth + size / 2 - right).toFixed(2));
        var left = POIEVariables.steckdosenLimits[type][3];
        if (value < left) {
          commit("changeDosen", {
            index: index,
            pos: "posx",
            value: left
          });
        }
        if (value > maxX) {
          commit("changeDosen", {
            index: index,
            pos: "posx",
            value: maxX
          });
        }
        var value = _ref.getters.getSteckdosen.dosens[index].posy;
        var type = _ref.getters.getSteckdosen.dosens[index].type - 1;
        var size = _ref.getters.getSteckdosen.dosens[index].size;
        var fullHeight = POIEVariables.dosenFullHeight[type];
        var top = POIEVariables.steckdosenLimits[type][0] - size / 2;
        var height = _ref.getters.getFormat.height;
        var maxY = parseFloat((height - fullHeight + size / 2 - top).toFixed(2));
        var bottom = POIEVariables.steckdosenLimits[type][2];
        if (value < bottom) {
          commit("changeDosen", {
            index: index,
            pos: "posy",
            value: bottom
          });
        }
        if (value > maxY) {
          commit("changeDosen", {
            index: index,
            pos: "posy",
            value: maxY
          });
        }
      } */
    },
    changeCropModeAction(mode) {
      this.changeCropMode(mode);
      if (mode) {
        this.activateBgDrag({});
      }
    },
    initEditor(data) {
      this.changeReady(0);
      this.setEditorDim({});
      var data = {
        uuid: this.getImage.original.imageId,
        background: 1
      };
      this.getResizedImageAction(data);
    },
    setEditorDim(data) {
      const o = this.getDefaults;
      var editor = jQuery(o.editor);
      this.changeEditorWidth(editor.width());
      this.changeEditorHeight(editor.height());
    },
    getResizedImageAction(data) {
      const editorFilesStore = useEditorFilesStore();
      const urlsStore = useUrlsStore();
      var image = this.getImage;
      if (!image.resized.inProgess) {
        this.inProgress(1);
        this.changeReady(0);

        const cache = editorFilesStore.findEditorFiles(data.uuid);

        if (!cache) {
          axios.instance
            .post(
              urlsStore.getUrl("getResizedImageUrl") + data.uuid,
              qs.stringify({})
            )
            .then(result => {
              this.inProgress(0);
              var response = result.data;
              if (response.success) {
                editorFilesStore.addEditorFile(response.data);
                if (typeof data.background != "undefined" && data.background) {
                  this.addImageToBackground({
                    data,
                    result: response.data
                  });
                } else {
                  this.addObjectOnCanvas({
                    data,
                    result: response.data
                  });
                }
              } else {
                alert(response.message);
              }
            })
            .catch(error => {
              console.log(error);
            });
        } else {
          if (typeof data.background != "undefined" && data.background) {
            this.addImageToBackground({
              data,
              result: cache
            });
          } else {
            this.addObjectOnCanvas({
              data,
              result: cache
            });
          }
        }
      }
    },
    addObjectOnCanvas({ result, data }) {
      const urlsStore = useUrlsStore();
      var editor = this.getEditor;
      var widthLayer = jQuery("#format-layer").width();
      var heightLayer = jQuery("#format-layer").height();
      var localWidth = 0;
      var localHeight = 0;
      var imageUnit = editor.dim.imageDimByFormat.unit;
      var canvasUnit = editor.dim.canvasDimByFormat.unit;
      var unit = imageUnit / canvasUnit;
      var imageRatio =
        result.thumbnails.working.size.height /
        result.thumbnails.working.size.width;
      if (widthLayer > heightLayer) {
        localWidth = parseInt(heightLayer / 3);
        localHeight = localWidth * imageRatio;
      } else {
        localHeight = parseInt(widthLayer / 3);
        localWidth = localHeight * imageRatio;
      }
      var params = {
        uuid: data.uuid,
        type: "image",
        subtype: "cropimage",
        width: localWidth / unit, //cm
        height: localHeight / unit, //cm
        left: data.left,
        bottom: data.top,
        imageSrc: getMediaUrl(
          result.thumbnails.working.url,
          urlsStore.getUrl("mediaBaseUrl")
        ),
        ratio: localWidth / localHeight,
        ratioImage: imageRatio,
        ratioWidth: result.size.width / result.thumbnails.working.size.width,
        ratioHeight: result.size.height / result.thumbnails.working.size.height,
        file_type: result.file_type,
        rotateAngle: 0,
        angle: 0,
        offsetX: 0,
        hasSnap: 1,
        offsetY: 0,
        zoom: 0,
        scaleX: 1
      };
      this.addObject(params);
      this.changeReady(1);
    },
    addImageToBackground({ result }) {
      const urlsStore = useUrlsStore();
      var image_src = getMediaUrl(
        result.thumbnails.working.url,
        urlsStore.getUrl("mediaBaseUrl")
      );
      this.changeUploadedFile(1);
      this.changeOriginalImageSize(result.size);
      this.changeResizedImageSize(result.thumbnails.working.size);
      this.changeResizedImageSrc(image_src);
      this.changeImageId(result.uuid);
      this.setEditorDim({});
      this.changeReady(1);
    },
    activateBgDrag() {
      var o = this.getDefaults;
      var imageLayer = jQuery(o.imageLayer);

      if (imageLayer.length) {
        var bgDraggable = imageLayer.data("printqBgDraggable");
        var editor = this.getEditor;
        if (!bgDraggable) {
          imageLayer.bgDraggable({
            drag: (function (that) {
              return function (event, ui) {
                var zoom = editor.zoom / 100; //cache this somewhere
                that.storeFocalPoint({ ui, zoom });
              };
            })(this),
            stop: (function (that) {
              return function (event, ui) {
                var zoom = editor.zoom / 100; //cache this somewhere
                that.storeFocalPoint({ ui, zoom });
              };
            })(this)
          });
        }
      }
    },
    updateBgOnResize(data) {
      var o = this.getDefaults;
      var imageLayer = jQuery(o.imageLayer);

      if (imageLayer.length) {
        var bgDraggable = imageLayer.data("printqBgDraggable");
        if (bgDraggable) {
          var ui = imageLayer.bgDraggable("updateOnResize");
          if (ui) {
            var zoom = this.getEditor.zoom / 100; //cache this somewhere
            this.storeFocalPoint({ ui, zoom });
          }
        }
      }
    },
    storeFocalPoint({ ui, zoom }) {
      var x = (ui.bgPosition.left * -1 + ui.elementSize.width / 2) / zoom;
      var y = (ui.bgPosition.top * -1 + ui.elementSize.height / 2) / zoom;
      var point = {
        x: x,
        y: y
      };

      this.changeFocalPoint(point);
    },
    setCanvasImage({ base64 }) {
      this.canvasImage = base64;
    },
    setImagesToLoad({ images }) {
      this.imagesToLoad = images;
    },
    parseChangeOptionResponse(data) {
      const webproductStore = useWebproductStore();
      var pageOptions = webproductStore.getActiveProductPageOptions;
      var margin = webproductStore.getActiveProductPageOptions;
      var o = this.getDefaults;
      R.forEachObjIndexed(
        (function (that, o) {
          return function (_option, _part) {
            if (_part == "format") {
              var selectedFormat = _option["selected"][0];
              var format_type = selectedFormat.split("custom_")[1];
              if (typeof format_type == "string") {
                var customValues = format_type.split("_");
                if (customValues.length == 2) {
                  that.doChangeFormat({
                    width: parseFloat(customValues[0]),
                    height: parseFloat(customValues[1])
                  });
                }
              } else {
                if (typeof _option.values != "undefined") {
                  R.forEach(
                    (function (that, selectedFormat) {
                      return function (format) {
                        if (format["value"] == selectedFormat) {
                          that.doChangeFormat({
                            width: parseFloat(format["attributes"]["width"]),
                            height: parseFloat(format["attributes"]["height"])
                          });
                        }
                      };
                    })(that, selectedFormat),
                    _option["values"]
                  );
                }
              }
            }
            var _value = _option["selected"][0];
            var margin_type = _value.split("oie_margin_type_")[1];
            if (typeof margin_type == "string") {
              var monochromatic_container = jQuery(
                o.monochromatic_container
              ).hide();
              var rounded_container = jQuery(o.rounded_container);
              that.changeCheckMonochromatic(0);
              if (
                that.getFormat.width == that.getFormat.height &&
                margin_type != "mirrored"
              ) {
                rounded_container.show();
              } else {
                that.changeRounded(0);
                jQuery(o.is_rotated_input).attr("checked", false);
                rounded_container.hide();
              }
              switch (margin_type) {
                case "enlarged":
                  that.changeMarginType(-1);
                  that.changeRandOption(0);
                  break;
                case "mirrored":
                  that.changeMarginSubtype(1);
                  that.changeMarginType(1);
                  that.changeRandOption(0);
                  break;
                case "white":
                  that.changeMarginSubtype(2);
                  that.changeMarginType(1);
                  that.changeMarginColor("#ffffff");
                  that.changeRandOption(1);
                  break;
                case "monochromatic":
                  that.changeMarginSubtype(3);
                  that.changeMarginType(1);
                  that.changeCheckMonochromatic(1);
                  monochromatic_container.show();
                  var monochromatic_input = jQuery(o.monochromatic_input);
                  that.changeMarginColor(monochromatic_input.val());
                  that.changeRandOption(0);
                  break;
              }
            }
            var margin_thickness = _value.split("oie_margin_thickness_")[1];
            if (typeof margin_thickness == "string") {
              margin_thickness = margin_thickness.replace("_", ".");
              margin_thickness = parseFloat(margin_thickness);
              if (!isNaN(margin_thickness))
                that.changeMargins({
                  top: margin_thickness,
                  right: margin_thickness,
                  bottom: margin_thickness,
                  left: margin_thickness
                });
            }
            //check for thickness
            var thickness = _value.split("oie_thickness_")[1];
            if (typeof thickness == "string") {
              thickness = thickness.replace("_", ".");
              thickness = parseFloat(thickness);
              if (!isNaN(thickness)) that.changeThickness(thickness);
            }
          };
        })(this, o),
        pageOptions
      );
    },
    initFormatPep(data) {
      var o = this.getDefaults;
      var format = jQuery(o.format);
      format
        .pep({
          constrainTo: "#oie-canvas",
          shouldEase: false,
          useCSSTranslation: false,
          useBoundingClientRect: true,
          shouldPreventDefault: false,
          start: (function (o) {
            return function () {
              var formatCropLayer = jQuery(o.formatCropLayer);
              formatCropLayer.addClass("invisibleBg");
            };
          })(o),
          drag: (function (o, that) {
            return function (ev, pep) {
              var formatLayer = pep.$el;
              var top = Math.round(parseFloat(formatLayer.css("top")));
              var left = Math.round(parseFloat(formatLayer.css("left")));
              var bottom = Math.round(parseFloat(formatLayer.css("bottom")));
              var right = Math.round(parseFloat(formatLayer.css("right")));

              if (isNaN(top)) {
                top = 0;
              }
              if (isNaN(left)) {
                left = 0;
              }
              if (isNaN(bottom)) {
                bottom = 0;
              }
              if (isNaN(right)) {
                right = 0;
              }

              that.changeCanvasFormatPositionByFormat({
                top: top,
                left: left,
                bottom: bottom,
                right: right
              });
              that.updateBgOnResize();
            };
          })(o, this),
          stop: (function (o, that) {
            return function (ev, pep) {
              var formatLayer = pep.$el;
              const top = Math.round(parseFloat(formatLayer.css("top")));
              const left = Math.round(parseFloat(formatLayer.css("left")));
              const bottom = Math.round(parseFloat(formatLayer.css("bottom")));
              const right = Math.round(parseFloat(formatLayer.css("right")));
              that.changeCanvasFormatPositionByFormat({
                top: top,
                left: left,
                bottom: bottom,
                right: right
              });
              var formatCropLayer = jQuery(o.formatCropLayer);
              formatCropLayer.removeClass("invisibleBg");
            };
          })(o, this)
        })
        .css(this.getFormatPosition);
    },
    initSteckDosenClicks(data) {
      var options = this.getDefaults;
      const widget = this;
      jQuery("#format-layer").droppable({
        accepts: ".steckdosen_item_draggable,.imageItem",
        drop: (function (that) {
          return function (event, ui) {
            var data = ui.draggable.data();
            if (ui.draggable.hasClass("steckdosen_item_draggable")) {
              var offset = jQuery("#format-layer").offset();
              var imageUnit = widget.getEditor.dim.imageDimByFormat.unit;
              var canvasUnit = widget.getEditor.dim.canvasDimByFormat.unit;
              var unit = imageUnit / canvasUnit;
              if (
                jQuery("#format-layer").height() <
                ui.position.top - offset.top + ui.helper.height()
              ) {
                ui.position.top =
                  jQuery("#format-layer").height() -
                  ui.helper.height() +
                  offset.top;
              }
              if (ui.position.top < offset.top) {
                ui.position.top = offset.top;
              }
              if (ui.position.left < offset.left) {
                ui.position.left = offset.left;
              }
              if (
                jQuery("#format-layer").width() <
                ui.position.left - offset.left + ui.draggable.width()
              ) {
                ui.position.left =
                  jQuery("#format-layer").width() -
                  ui.helper.width() +
                  offset.left;
              }
              var posx = (ui.position.left - offset.left) / unit;
              var posy =
                (jQuery("#format-layer").height() -
                  ui.helper.height() -
                  (ui.position.top - offset.top)) /
                unit;
              var info = data.info;
              that.addNewDosen({
                code: info.code,
                name: info.name,
                imageSrc: info.image_url,
                width: info.width,
                height: info.height,
                offsetx: parseFloat(info.offsetx),
                offsety: parseFloat(info.offsety),
                left: posx,
                bottom: posy
              });
            }
            if (ui.draggable.hasClass("imageItem")) {
              var offset = jQuery("#format-layer").offset();
              var imageUnit = that.getEditor.dim.imageDimByFormat.unit;
              var canvasUnit = that.getEditor.dim.canvasDimByFormat.unit;
              var unit = imageUnit / canvasUnit;
              if (
                jQuery("#format-layer").height() <
                ui.position.top - offset.top + ui.helper.height()
              ) {
                ui.position.top =
                  jQuery("#format-layer").height() -
                  ui.helper.height() +
                  offset.top;
              }
              if (ui.position.top < offset.top) {
                ui.position.top = offset.top;
              }
              if (ui.position.left < offset.left) {
                ui.position.left = offset.left;
              }
              if (
                jQuery("#format-layer").width() <
                ui.position.left - offset.left + ui.draggable.width()
              ) {
                ui.position.left =
                  jQuery("#format-layer").width() -
                  ui.helper.width() +
                  offset.left;
              }
              var posx = (ui.position.left - offset.left) / unit;
              var posy =
                (jQuery("#format-layer").height() -
                  ui.helper.height() -
                  (ui.position.top - offset.top)) /
                unit;
              var image = data.image;
              var dataToSend = {
                uuid: image.uuid,
                left: posx,
                top: posy,
                background: that.getEditor.bckMode | 0
              };
              that.getResizedImageAction(dataToSend);
            }
          };
        })(this)
      });
      jQuery("body").on("mouseup", function () {
        var format = jQuery("#format-layer");
        if (format.length) {
          if (format.data("plugin_pep") != "undefined") {
            var data = format.data("plugin_pep");
            data.disableMove = false;
            format.data("plugin_pep", data);
          }
        }
      });
    },
    checkItemPosition(data) {
      const objects = this.getObject.objects;
      const stekdosens = this.getSteckdosen.dosens;
      const cutouts = this.getCutouts.cutouts;
      const format = this.getFormat;
      const that = this;

      objects.forEach((item, index) => {
        computeNewPositionforItem(item, "objects", index, format, that);
      });
      stekdosens.forEach((item, index) => {
        computeNewPositionforItem(item, "steckdosen", index, format, that);
      });
      cutouts.forEach((item, index) => {
        computeNewPositionforItem(item, "cutouts", index, format, that);
      });
    }
  }
});

function computeNewPositionforItem(item, type, index, format, that) {
  const { width, height, bottom, left, offsetX, offsetY } = item;
  const topDiff =
    format.height - (bottom + parseFloat(height) - parseFloat(offsetY));
  const leftDiff =
    format.width - (left + parseFloat(width) - parseFloat(offsetX));
  let newBottom = bottom;
  let newLeft = left;
  if (topDiff < 0) {
    newBottom = bottom + topDiff;
    if (newBottom < 0) newBottom = 0;
  }
  if (leftDiff < 0) {
    newLeft = left + leftDiff;
    if (newLeft < 0) newLeft = 0;
  }

  if (newBottom != bottom || newLeft != left) {
    const data = {
      bottom: newBottom,
      left: newLeft
    };
    that.updateItem({
      type: type,
      index: index,
      props: data
    });
  }
}
